/**
 * Le markdown **limité** des messages (docs/13 §6).
 *
 * Le corps d'un message est stocké en texte brut et le reste : ce module n'en
 * produit pas du HTML, il en produit un **arbre typé** dont chaque nœud est
 * l'un des cinq que déclare ce fichier. C'est la forme de l'allowlist que le
 * document demande — non pas « quelles balises retirer », qui est une liste à
 * tenir à jour, mais « quels nœuds existent », qui est une liste fermée par le
 * type. Un rendu ne peut alors afficher que ce qui est ici, et
 * `dangerouslySetInnerHTML` n'a nulle part où entrer.
 *
 * **Les liens ne sont jamais étiquetés.** La syntaxe `[texte](url)` est
 * volontairement absente : elle laisse écrire une étiquette qui ne dit pas où
 * elle mène, alors que docs/13 §6 demande l'inverse — « domaines externes
 * affichés en entier ». Une URL reconnue dans le texte devient donc un lien
 * dont le libellé **est** l'adresse, et il n'existe aucune façon d'écrire un
 * lien dont le texte et la destination diffèrent.
 *
 * **Deux schémas, et pas un de plus.** Une adresse n'est reconnue que si elle
 * commence par `http://` ou `https://` : `javascript:`, `data:` et les autres
 * ne peuvent pas être *produits* par ce parseur, indépendamment de ce que le
 * rendu en ferait. La garde est dans l'expression qui reconnaît, pas dans un
 * contrôle posé après coup.
 */

export type Inline =
  | { kind: "text"; text: string }
  | { kind: "bold"; children: Inline[] }
  | { kind: "italic"; children: Inline[] }
  | { kind: "link"; href: string };

export type Block =
  | { kind: "paragraph"; children: Inline[] }
  | { kind: "bulletList"; items: Inline[][] }
  | { kind: "orderedList"; items: Inline[][] };

/**
 * Les quatre formes reconnues, dans l'ordre où elles sont essayées.
 *
 * `**gras**` avant `*italique*` : l'inverse ferait lire `**mot**` comme un
 * italique vide suivi d'un italique vide. L'ordre des alternatives d'une
 * expression régulière est une décision, pas une mise en page.
 *
 * L'URL s'arrête avant une ponctuation finale : « voir https://exemple.fr. »
 * ne doit pas produire un lien vers `exemple.fr.`, qui ne résout pas.
 */
const INLINE_PATTERN =
  /\*\*([^*\n]+)\*\*|\*([^*\n]+)\*|_([^_\n]+)_|(https?:\/\/[^\s<>"']+[^\s<>"'.,;:!?)])/g;

const BULLET = /^[-*]\s+(.*)$/;
const ORDERED = /^\d+[.)]\s+(.*)$/;

function inlines(text: string): Inline[] {
  const nodes: Inline[] = [];
  let cursor = 0;

  // `matchAll` plutôt qu'une boucle sur `exec` : l'expression est globale, et
  // une boucle qui oublierait de remettre `lastIndex` à zéro entre deux appels
  // sauterait le premier motif du message suivant — un défaut qui ne se voit
  // qu'au second message affiché.
  for (const match of text.matchAll(INLINE_PATTERN)) {
    const at = match.index;
    if (at > cursor) {
      nodes.push({ kind: "text", text: text.slice(cursor, at) });
    }
    const [whole, bold, italicStar, italicUnderscore, url] = match;
    if (bold !== undefined) {
      nodes.push({ kind: "bold", children: inlines(bold) });
    } else if (italicStar !== undefined) {
      nodes.push({ kind: "italic", children: inlines(italicStar) });
    } else if (italicUnderscore !== undefined) {
      nodes.push({ kind: "italic", children: inlines(italicUnderscore) });
    } else if (url !== undefined) {
      nodes.push({ kind: "link", href: url });
    }
    cursor = at + whole.length;
  }

  if (cursor < text.length) {
    nodes.push({ kind: "text", text: text.slice(cursor) });
  }
  return nodes;
}

/**
 * Le corps d'un message → des blocs.
 *
 * Les sauts de ligne **à l'intérieur** d'un paragraphe sont conservés dans le
 * texte et rendus par `white-space: pre-wrap` : un message écrit sur trois
 * lignes s'affiche sur trois lignes, et le faire par découpage produirait des
 * clés de liste sur du contenu utilisateur.
 */
export function parseMessage(body: string): Block[] {
  const blocks: Block[] = [];
  const lines = body.split("\n");
  let paragraph: string[] = [];
  let bullets: string[] = [];
  let ordered: string[] = [];

  const flush = (): void => {
    if (paragraph.length > 0) {
      blocks.push({ kind: "paragraph", children: inlines(paragraph.join("\n")) });
      paragraph = [];
    }
    if (bullets.length > 0) {
      blocks.push({ kind: "bulletList", items: bullets.map(inlines) });
      bullets = [];
    }
    if (ordered.length > 0) {
      blocks.push({ kind: "orderedList", items: ordered.map(inlines) });
      ordered = [];
    }
  };

  for (const line of lines) {
    const bullet = BULLET.exec(line);
    const numbered = ORDERED.exec(line);
    if (bullet !== null) {
      if (paragraph.length > 0 || ordered.length > 0) flush();
      bullets.push(bullet[1] ?? "");
    } else if (numbered !== null) {
      if (paragraph.length > 0 || bullets.length > 0) flush();
      ordered.push(numbered[1] ?? "");
    } else if (line.trim() === "") {
      flush();
    } else {
      if (bullets.length > 0 || ordered.length > 0) flush();
      paragraph.push(line);
    }
  }
  flush();
  return blocks;
}

/**
 * Le lien sort-il de la plateforme ?
 *
 * Un lien interne n'a pas à passer par l'interstitiel : l'utilisateur ne quitte
 * rien, et interposer une confirmation sur chaque lien finirait par la faire
 * cliquer sans la lire — ce qui la retirerait aussi de ceux qui sortent.
 *
 * Une URL que `URL` refuse est traitée comme **externe** : le doute se tranche
 * du côté qui prévient.
 */
export function isExternal(href: string, origin: string): boolean {
  try {
    return new URL(href).origin !== origin;
  } catch {
    return true;
  }
}
